import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/shared/components/ui/Card';
import { buildRoute } from '@/shared/lib/constants';
import type { PondAttentionItem, Severity } from '../types';
import { getSeverityBadgeClass, getAttentionReasonLabel, getNavigationTarget } from '../types';

interface PondsAttentionCardProps {
  farmId: string;
  ponds: PondAttentionItem[];
}

const ITEMS_TO_SHOW = 5;

export const PondsAttentionCard: React.FC<PondsAttentionCardProps> = ({ farmId, ponds }) => {
  const [showAll, setShowAll] = useState(false);

  // Group ponds by severity
  const groupedBySeverity = ponds.reduce((acc, pond) => {
    const severity = pond.severity || 'medium';
    if (!acc[severity]) {
      acc[severity] = [];
    }
    acc[severity].push(pond);
    return acc;
  }, {} as Record<Severity, PondAttentionItem[]>);

  const severityOrder: Severity[] = ['critical', 'high', 'medium', 'low'];
  const visiblePonds = showAll ? ponds : ponds.slice(0, ITEMS_TO_SHOW);
  const hasMore = ponds.length > ITEMS_TO_SHOW;

  // Get route based on attention reason
  const getPondRoute = (pond: PondAttentionItem): string => {
    const target = pond.details?.navigateTo || getNavigationTarget(pond.primaryReason);
    switch (target) {
      case 'water-quality':
        return buildRoute.waterQuality(farmId, pond.pondId);
      case 'feed':
        return buildRoute.feed(farmId, pond.pondId);
      case 'fish-health':
        return buildRoute.fishHealth(farmId, pond.pondId);
      case 'maintenance':
        return buildRoute.maintenance(farmId, pond.pondId);
      case 'ai':
        return `/farms/${farmId}/ai`;
      default:
        return `/farms/${farmId}/ponds/${pond.pondId}`;
    }
  };

  if (ponds.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          <CardTitle>Ponds Needing Attention</CardTitle>
          <span className="ml-auto text-sm text-gray-500">{ponds.length} pond{ponds.length !== 1 ? 's' : ''}</span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {severityOrder.map((severity) => {
            const severityPonds = groupedBySeverity[severity];
            if (!severityPonds || severityPonds.length === 0) return null;

            const visibleSeverityPonds = showAll
              ? severityPonds
              : severityPonds.filter((p) => visiblePonds.includes(p));

            if (visibleSeverityPonds.length === 0) return null;

            return (
              <div key={severity}>
                {/* Severity header */}
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-2 py-0.5 text-xs font-semibold rounded-full uppercase ${getSeverityBadgeClass(severity)}`}>
                    {severity}
                  </span>
                </div>

                {/* Ponds list */}
                <div className="space-y-2 mb-3">
                  {visibleSeverityPonds.map((pond) => (
                    <Link
                      key={pond.pondId}
                      to={getPondRoute(pond)}
                      className="flex items-center justify-between p-3 rounded-lg border border-slate-200/50 hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 truncate">{pond.pondName}</span>
                          {pond.fishType && (
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                              {pond.fishType}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-0.5">
                          {getAttentionReasonLabel(pond.primaryReason)}
                          {pond.details?.hoursSinceTest && ` (${pond.details.hoursSinceTest}h)`}
                          {pond.details?.mortalityCount && ` (${pond.details.mortalityCount} fish)`}
                          {pond.details?.lastAppetiteRating && ` (rating: ${pond.details.lastAppetiteRating}/5)`}
                        </p>
                        {/* Additional reasons as small badges */}
                        {pond.reasons && pond.reasons.length > 1 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {pond.reasons.slice(1).map((r, idx) => (
                              <span
                                key={idx}
                                className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded"
                              >
                                {r.label}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Show more/less toggle */}
          {hasMore && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium mt-2"
            >
              {showAll ? (
                <>
                  <ChevronUp className="h-4 w-4" />
                  Show less
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4" />
                  View all ({ponds.length - ITEMS_TO_SHOW} more)
                </>
              )}
            </button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
